// Client-side auth helpers — talk to the Nest auth controller at /api/auth/*.
//
// Microsoft Entra is the sole authentication path (ADR-0004 / issue #4) —
// there is no demo bypass. `signIn` triggers the full-page Entra flow.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ActingForBook, SessionFeatures } from '@septima/contract';
import { apiFetch, apiUrl } from './api';
import { safeRedirect } from './safe-redirect';
import { appPath } from './app-path';

export interface SignInResult {
    ok: boolean;
    error?: string;
}

export interface SessionState {
    authenticated: boolean;
    user: {
        userId: string;
        email: string | null;
        name: string | null;
        authMethod: 'microsoft';
        /** The view keys the allowlist grants this user (resolved at callback). */
        personas?: string[];
        /**
         * The currently-active persona (ADR-0004). One persona governs every
         * read; switching it is a deliberate beat audited via OpsAuditLog.
         * The sidebar reads this to highlight the active row and persist the
         * persona across a hard reload.
         */
        activePersona?: string;
        /**
         * The IM books this user may act on. An assistant assigned to
         * several IMs gets one sidebar button for each, in place of the single
         * "Desk" button. Empty for a user who works their own book.
         */
        actingFor?: ActingForBook[];
        /**
         * The IM book the session reads now. The server resolves it from the
         * cookie against the assignments, so it is always a book the switcher
         * offers, or null when the user acts for nobody.
         */
        activeActingForUserId?: string | null;
    } | null;
    microsoftConfigured: boolean;
    /** Dev-only skip-auth hatch is enabled on the server (NODE_ENV !== prod
     *  AND DEV_AUTH_SKIP=true). Surfaces the dev fixture sign-in shortcut on
     *  the login screen. Always false in production. */
    devAuthSkipEnabled?: boolean;
    /** The server booted in scenario mode (internal sandbox stack). Gates the
     *  scenario chrome (top bar + Scenario Centre). Always false in production. */
    scenarioMode?: boolean;
    /** Scenario clock offset (ms) — mirrored into the web clock so time-derived
     *  rendering advances with the scenario server. 0 outside scenario mode. */
    scenarioClockOffsetMs?: number;
    /** The hash of the Feature Manifest of the API and the AI seats of the
     *  user (ADR-0027). The shell compares the hash with its own build. */
    features?: SessionFeatures;
}

export async function fetchSession(): Promise<SessionState> {
    try {
        const res = await apiFetch('/api/auth/session', { credentials: 'include' });
        if (!res.ok) {
            return {
                authenticated: false,
                user: null,
                microsoftConfigured: false,
                devAuthSkipEnabled: false,
                scenarioMode: false,
            };
        }
        return (await res.json()) as SessionState;
    } catch {
        return {
            authenticated: false,
            user: null,
            microsoftConfigured: false,
            devAuthSkipEnabled: false,
            scenarioMode: false,
        };
    }
}

/**
 * Shared React Query key for the signed-in session. Every consumer
 * (SessionProvider, the `/` auth gate, the login screen) reads through this
 * one key so React Query collapses them to a single in-flight request and a
 * single cache entry — instead of each firing its own `fetchSession`.
 */
export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

/**
 * The session as a React Query. `fetchSession` never rejects (it maps any
 * failure to an unauthenticated state), so `data` is the source of truth and
 * `isPending` marks the initial hydration window. Consumers share the cache
 * via {@link SESSION_QUERY_KEY}; invalidate that key to force a refresh after
 * a persona switch.
 */
export function useSessionQuery(): UseQueryResult<SessionState> {
    return useQuery<SessionState>({
        queryKey: SESSION_QUERY_KEY,
        queryFn: fetchSession,
        // The session drives an access-control input (the persona switcher +
        // client route-grant), so it must self-correct rather than trust the
        // global 5-minute `staleTime` / `refetchOnWindowFocus: false` defaults.
        // A grant changed server-side (or a cookie swapped under a live SPA)
        // re-syncs on the next tab focus. The endpoint is tiny; refetching it is
        // cheap. `staleTime: 0` marks it always-stale so the focus refetch fires.
        staleTime: 0,
        refetchOnWindowFocus: true,
    });
}

/**
 * Switch the IM book an assistant reads. It mirrors
 * {@link switchPersona}: the server validates the book against the acting-for
 * directory, records the change in the audit log, and the shell refreshes the
 * session afterwards.
 *
 * The request is `keepalive`, because the selected book is a fact of the
 * server session, and it arrives on the `Set-Cookie` header of this response.
 * A reader who presses the switcher and then opens another Desk page at once
 * starts a document navigation, and the browser cancels each normal request
 * that is in flight. The switch was then lost, and the loss was silent: the
 * sidebar kept the optimistic paint of the new book, while every read on the
 * next page stayed on the previous book. A `keepalive` request lives longer
 * than the document, so the cookie still lands. The body holds some tens of
 * bytes, which is far below the 64 KiB that `keepalive` permits.
 */
export async function switchActingFor(
    userId: string,
): Promise<{ ok: boolean; status: number; error?: string }> {
    try {
        const res = await apiFetch('/api/auth/acting-for', {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ userId }),
        });
        if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { message?: string };
            return {
                ok: false,
                status: res.status,
                error: body.message ?? `acting_for_switch_failed_${res.status}`,
            };
        }
        return { ok: true, status: res.status };
    } catch (err) {
        return {
            ok: false,
            status: 0,
            error: err instanceof Error ? err.message : 'acting_for_switch_failed',
        };
    }
}

/** A fixture user surfaced by the hardened dev fixture sign-in route. */
export interface DevFixtureUser {
    id: string;
    name: string;
    email: string;
    personas: string[];
}

/**
 * DEV-ONLY: list the fixture users the server will accept. Returns an empty
 * array if the dev fixture sign-in hatch is not enabled (the endpoint 404s),
 * so the UI can simply hide the dev section in that case.
 */
export async function listDevFixtureUsers(): Promise<DevFixtureUser[]> {
    try {
        const res = await apiFetch('/api/_dev/local-fixture-users', {
            credentials: 'include',
        });
        if (!res.ok) return [];
        const data = (await res.json()) as { users?: DevFixtureUser[] };
        return data.users ?? [];
    } catch {
        return [];
    }
}

/**
 * DEV-ONLY: mint a session by hitting the hardened dev fixture sign-in
 * route. The route accepts only a `userId` matching an Active `users`
 * directory row (the same allowlist that gates Entra logins); personas come
 * from that row, never the body. The route 404s in production, off-host, or
 * when DEV_AUTH_SKIP is unset (issue #89).
 */
export async function signInAsDevFixture(userId: string): Promise<SignInResult> {
    try {
        const res = await apiFetch('/api/_dev/local-fixture-signin', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ userId: userId }),
        });
        if (!res.ok) return { ok: false, error: `dev_signin_failed_${res.status}` };
        return { ok: true };
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'dev_signin_failed' };
    }
}

/**
 * Trigger the Microsoft Entra sign-in flow. Full-page navigates to the
 * server's /microsoft/start, which sets a flow cookie and 302s on to
 * Microsoft. Microsoft Entra is the only authentication path.
 */
export async function signIn(options?: { callbackUrl?: string }): Promise<SignInResult> {
    const callbackUrl = options?.callbackUrl ?? '/';

    if (typeof window === 'undefined') return { ok: false, error: 'no_window' };
    // Use apiUrl() so the redirect targets the server origin in a deployed
    // cross-origin build (web + server on different domains).
    const url = new URL(apiUrl('/api/auth/microsoft/start'), window.location.origin);
    url.searchParams.set('callbackUrl', callbackUrl);
    window.location.assign(url.toString());
    return { ok: true };
}

/**
 * Switch the active persona for the current session (ADR-0004 /
 * issue #100). The server validates the requested persona against the
 * granted list, updates the session cookie, and writes an OpsAuditLog
 * row recording the switch. On success the caller should refresh the
 * session context so the rest of the app sees the new persona.
 */
export async function switchPersona(
    persona: string,
): Promise<{ ok: boolean; status: number; error?: string }> {
    try {
        const res = await apiFetch('/api/auth/persona', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ persona }),
        });
        if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as {
                message?: string;
            };
            return {
                ok: false,
                status: res.status,
                error: body.message ?? `persona_switch_failed_${res.status}`,
            };
        }
        return { ok: true, status: res.status };
    } catch (err) {
        return {
            ok: false,
            status: 0,
            error: err instanceof Error ? err.message : 'persona_switch_failed',
        };
    }
}

export async function signOut(options?: { callbackUrl?: string }): Promise<void> {
    try {
        await apiFetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
        });
    } catch {
        // Best-effort; cookie clear works even if the request fails because the
        // page will navigate away anyway.
    }
    const target = safeRedirect(options?.callbackUrl) || '/login';
    if (typeof window !== 'undefined') {
        window.location.assign(appPath(target));
    }
}
